"use client";

import { useState, useSyncExternalStore, type FormEvent } from "react";
import { Bell, Check, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api-client";
import { reportError } from "@/lib/client-log";
import { isEligibleForCalendar } from "@/lib/calendar";
import type { CiudadVivaEvent } from "@/lib/events";
import { LOCALE_META } from "@/lib/i18n/config";
import { useLocale, useMessages } from "@/lib/i18n/client";
import { addDays, nextRemindableDate, REMINDER_HOUR, supportsReminder } from "@/lib/reminders";
import { useAuthStore } from "@/lib/store/auth-store";
import { track } from "@/lib/tracking/track";
import messages from "./EventReminder.i18n";

type Status = "pending" | "confirmed" | "already";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const noSubscribe = () => () => {};

// "Avísame": correo el día antes del evento, sin cuenta (con confirmación
// por correo). Independiente de "Me interesa", que es una métrica para el
// organizador. La ficha se cachea (ISR), así que "si todavía alcanza" se
// calcula en el navegador, no al generar la página.
export function EventReminder({ event }: { event: CiudadVivaEvent }) {
  const t = useMessages(messages);
  const lang = useLocale();
  const user = useAuthStore((s) => s.user);
  // undefined en el servidor; en el navegador, la función avisable o null.
  const occurrence = useSyncExternalStore(
    noSubscribe,
    () => nextRemindableDate(event),
    () => undefined,
  );
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<Status | null>(null);
  const [remindOn, setRemindOn] = useState<string | null>(null);

  if (!supportsReminder(event) || occurrence === undefined) return null;

  const enabled = occurrence !== null;
  const help = !enabled
    ? isEligibleForCalendar(event) ? t.tooSoonCalendar : t.tooSoon
    : user ? t.helpLoggedIn : t.help;

  const formatDay = (ymd: string) =>
    new Date(`${ymd}T00:00:00Z`).toLocaleDateString(LOCALE_META[lang].intl, {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });

  async function send(withEmail?: string) {
    setSending(true);
    setError("");
    try {
      const res = withEmail
        ? await api.post<{ status: Status; occurrence: string }>("/reminders", {
            slug: event.slug,
            email: withEmail,
            language: lang,
          })
        : await api.post<{ status: Status; occurrence: string }>("/reminders/mine", {
            slug: event.slug,
            language: lang,
          });
      setStatus(res.status);
      setRemindOn(addDays(res.occurrence, -1));
      setOpen(false);
      track("reminder_request", { slug: event.slug, logged_in: !withEmail, status: res.status });
    } catch (err) {
      const httpStatus = err instanceof ApiError ? err.status : undefined;
      if (httpStatus === 429) {
        setError(t.rateLimited);
      } else {
        reportError("event-reminder", err, { httpStatus });
        setError(httpStatus === 400 ? t.tooSoon : t.error);
      }
    } finally {
      setSending(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError(t.invalidEmail);
      return;
    }
    send(value);
  }

  if (status) {
    const text =
      status === "pending"
        ? t.pending
        : status === "already"
          ? t.already
          : t.confirmed(formatDay(remindOn ?? occurrence ?? ""), REMINDER_HOUR);
    return (
      <div className="mt-5 flex items-start gap-2.5 rounded-2xl border border-border bg-muted/40 p-4 text-sm" role="status">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>{text}</p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      {open ? (
        <form onSubmit={onSubmit} className="space-y-2" noValidate>
          <label htmlFor="reminder-email" className="text-sm font-medium">
            {t.emailLabel}
          </label>
          <input
            id="reminder-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
            className="h-11 w-full rounded-full border border-border bg-background px-4 text-sm outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError("");
              }}
              className="h-11 flex-1 rounded-full border border-border text-sm font-semibold hover:bg-muted"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={sending}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              {t.submit}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          disabled={!enabled || sending}
          title={help}
          onClick={() => (user ? send() : setOpen(true))}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border border-border text-sm font-semibold hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-transparent"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          {t.button}
        </button>
      )}
      {error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{help}</p>
      )}
    </div>
  );
}
